import { create } from 'zustand';
import {
  getReservationBookingHistoriesApi,
  ReservationBookingHistoriesParams,
} from 'app/services/reservationHistoryApi';
import { showReservationErrorToast } from '@/lib/utils';

export type ReservationHistoryPagination = {
  pageIndex: number;
  pageSize: number;
};

export enum ReservationHistoryStatus {
  PROCESSING = 'Processing',
  ON_HOLD = 'OnHold',
  CONFIRMED = 'Confirmed',
  SEATED = 'Seated',
  COMPLETED = 'Completed',
  PENDING_APPROVAL = 'PendingApprove',
  CANCELLED = 'Cancelled',
  REJECTED = 'Rejected',
  NO_SHOW = 'NoShow',
  EXPIRED = 'Expired',
}

export enum ReservationHistory_PaymentStatus {
  COMPLETED = 'Completed',
  FAILED = 'Failed',
  PENDING = 'Pending',
  REFUNDED = 'Refunded',
  EXPIRED = 'Expired',
  CANCELLED = 'Cancelled',
}

export enum ReservationHistory_ApproveStatus {
  PENDING_APPROVAL = 'PendingApprove',
  APPROVED = 'Approved',
  PAYMENT_REJECTED = 'PaymentRejected',
}

export type ReservationHistoryFilter = {
  orderId?: string;
  reservationNo?: string;
  bookingDateFrom?: string;
  bookingDateTo?: string;
  reservationDateFrom?: string;
  reservationDateTo?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  usageDateFrom?: string;
  usageDateTo?: string;
  storeId?: string;
  storeName?: string;
  agentId?: string;
  status?: ReservationHistoryStatus[];
  paymentStatus?: string;
};

export type ReservationHistoryItem = {
  id?: string | number;
  orderId?: string | number;
  reservationNo?: string | number;
  code?: string;
  bookingCode?: string;
  reservationCode?: string;
  date?: string;
  bookingDate?: string;
  reservationDate?: string;
  restaurant?: string;
  storeName?: string;
  restaurantName?: string;
  status?: ReservationHistoryStatus | string | number;
  statusName?: string;
  statusNameKey?: string;
  paymentStatus?: string;
  reservationTime?: string;
  time?: string;
  partySize?: string | number;
  guestAmount?: string | number;
  quantity?: string | number;
  saleContext?: {
    salesAgent?: string;
    [key: string]: any;
  };
  customerName?: string;
  createdBy?: string;
  createdByName?: string;
  paymentMethod?: string;
  totalDeposit?: string | number;
  currency?: {
    value?: string
    exchangeRate?: any
  };
  cancelDate?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancelReason?: string;
  penaltyAmount?: string | number;
  price?: string | number;
  totalAmount?: string | number;
  totalPaid?: string | number;
  invoiceCode?: string;
  [key: string]: any;
  paidAmount?: number
  clientPaymentAmount?: number
  payment: any
};

type ReservationHistoryState = {
  data: ReservationHistoryItem[];
  totalCount: number;
  statusCounts: Record<string, number>;
  filter: ReservationHistoryFilter;
  pagination: ReservationHistoryPagination;
  isLoadingDataTable: boolean;
  setFilter: (filter: ReservationHistoryFilter) => void;
  fetchHistories: (
    update?:
      | ReservationHistoryPagination
      | ((prev: ReservationHistoryPagination) => ReservationHistoryPagination),
    params?: ReservationBookingHistoriesParams,
  ) => Promise<void>;
  handlePaginationChange: (page: number, pageSize: number) => void;
};

const getResponsePayload = (response: any) => response?.data?.data ?? response?.data;
const getResponseStatusTotalPayload = (response: any) => response?.data?.metadata ?? response?.data;

const getResponseItems = (payload: any): ReservationHistoryItem[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.list)) return payload.list;

  return [];
};

const getResponseTotal = (payload: any, items: ReservationHistoryItem[]) =>
  Number(
    payload?.totalCount ??
    payload?.total ??
    payload?.totalElements ??
    payload?.totalRecords ??
    payload?.count ??
    items.length,
  );

export const useReservationHistoryStore = create<ReservationHistoryState>()(
  (set, get) => ({
    data: [],
    totalCount: 0,
    statusCounts: {},
    filter: {},
    pagination: { pageIndex: 0, pageSize: 25 },
    isLoadingDataTable: false,
    setFilter: filter => set({ filter }),
    handlePaginationChange: (page, pageSize) => {
      get().fetchHistories({
        pageIndex: page - 1,
        pageSize,
      });
    },
    fetchHistories: async (update, params) => {
      const current = get().pagination;
      const next =
        typeof update === 'function'
          ? update(current)
          : update ?? get().pagination;
      const safePagination = {
        pageIndex: next.pageSize !== current.pageSize ? 0 : next.pageIndex,
        pageSize: next.pageSize,
      };
      const filter = params ?? get().filter;

      try {
        set({ isLoadingDataTable: true });

        const response = await getReservationBookingHistoriesApi({
          ...filter,
          status: filter.status?.length
            ? filter.status.join(',')
            : '',
          pageIndex: safePagination.pageIndex,
          page: safePagination.pageIndex + 1,
          pageSize: safePagination.pageSize,
        });
        const payload = getResponsePayload(response);
        const items = getResponseItems(payload);
        const payloadTotalStatues = getResponseStatusTotalPayload(response)
        const statusTotals = payloadTotalStatues.statusTotals || {
          Cancelled: 0,
          Completed: 0,
          Confirmed: 0,
          Expired: 0,
          NoShow: 0,
          OnHold: 0,
          PendingApprove: 0,
          Processing: 0,
          Rejected: 0,
          Seated: 0,
        }

        set({
          data: items,
          totalCount: getResponseTotal(payload, items),
          statusCounts: statusTotals,
          filter,
          pagination: safePagination,
          isLoadingDataTable: false,
        });
      } catch (error) {
        showReservationErrorToast(error);
        set({
          data: [],
          totalCount: 0,
          statusCounts: {},
          filter,
          pagination: safePagination,
          isLoadingDataTable: false,
        });
      }
    },
  }),
);
